import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from '../../utils/darkSwal';
import { getUser } from '../../utils/authStorage';
import BASE_URL from '../../utils/api';
import PageHeading from '../PageHeading';
import Div from '../Div';
import Spacing from '../Spacing';
import { useNavigate } from 'react-router-dom';

export default function CheckOutPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  const user = getUser();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (user && token) {
      fetchUser();
    }
    fetchProduct();
  },[]);

  const fetchProduct = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/products/${productId}`);
        const data = await res.json();
        setProduct(data);
      } catch (error) {
        Swal.fire('Error', 'Failed to load product details', 'error');
      }
    };

    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/users/${user._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setUserDetails(data);
      } catch (error) {
        Swal.fire('Error', 'Failed to load user details', 'error');
      }
    };

  const handleQuantityChange = (e) => {
    const val = parseInt(e.target.value);
    if (val > product.productQty) {
      Swal.fire('Warning', 'Exceeds available quantity', 'warning');
      return;
    }
    setQuantity(val);
  };

  const handleOrder = async () => {
    if (!user || !token) {
      Swal.fire('Unauthorized', 'Please log in first', 'warning');
      return;
    }

    Swal.fire({ title: 'Placing Order...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const res = await fetch(`${BASE_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user._id,
          productId,
          quantity,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        Swal.fire('Success', data.message || 'Order placed successfully!', 'success').then(() => {
          navigate('/order-details');
        });
      } else {
        Swal.fire('Error', data.message || 'Failed to place order', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network issue. Try again.', 'error');
    }
  };

  const calculateTotal = () => {
    if (!product) return 0;
    const price = parseFloat(product.productPrice.$numberDecimal);
    const discountPercent = parseFloat(product.productDiscount.$numberDecimal);

    const discountedPrice = price - (price * discountPercent / 100);
    return discountedPrice * quantity;
  };

  return (
    <>
      <PageHeading title="Checkout" bgSrc="/images/portfolio_hero_bg.jpeg" pageLinkText="Checkout" />
      <Spacing lg="80" md="60" />
      <Div className="checkout-page container">
        {product && userDetails ? (
          <div className="checkout-grid">
            {/* Billing Details */}
            <div className="checkout-section">
              <h3>Billing Details</h3>
              <p><strong>Name:</strong> {userDetails.userName}</p>
              <p><strong>Email:</strong> {userDetails.email}</p>
              <p><strong>Phone:</strong> {userDetails.phoneNumber}</p>
              <p><strong>Address:</strong> {userDetails.address}</p>
            </div>

            {/* Payment Method */}
            <div className="checkout-section">
              <h3>Payment Method</h3>
              <p><strong>Bank Name:</strong> NDB Bank</p>
              <p><strong>Branch:</strong> Kandana</p>
              <p><strong>Account No:</strong> 111000316136</p>
              <p>Please transfer and confirm payment to complete the order.</p>
            </div>

            {/* Order Summary */}
            <div className="checkout-summary">
              <h3>Order Summary</h3>

              {/* Table for Desktop */}
              <div className="checkout-table-wrapper">
                <table className="checkout-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Qty</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{product.productName}</td>
                      <td>Rs. {product.productPrice.$numberDecimal}</td>
                      <td>{product.productDiscount.$numberDecimal}%</td>
                      <td>
                        <input
                          type="number"
                          value={quantity}
                          min={1}
                          max={product.productQty}
                          onChange={handleQuantityChange}
                          className="checkout-qty-input"
                        />
                      </td>
                      <td>Rs. {calculateTotal().toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Card for Mobile */}
              <div className="checkout-card-wrapper">
                <div className="checkout-order-card">
                  <h4>{product.productName}</h4>
                  <p><strong>Price:</strong> Rs. {product.productPrice.$numberDecimal}</p>
                  <p><strong>Discount:</strong> {product.productDiscount.$numberDecimal}%</p>
                  <div className="checkout-qty">
                    <label><strong>Quantity:</strong></label>
                    <input
                      type="number"
                      value={quantity}
                      min={1}
                      max={product.productQty}
                      onChange={handleQuantityChange}
                      className="checkout-qty-input"
                    />
                  </div>
                  <p><strong>Total:</strong> Rs. {calculateTotal().toFixed(2)}</p>
                </div>
              </div>

              <div className="checkout-action">
                <button className="cs-btn cs-style1" onClick={handleOrder}>
                  Complete Order
                </button>
              </div>
            </div>

          </div>
        ) : (
          <p className="text-center">Loading checkout information...</p>
        )}
      </Div>

      <Spacing lg="80" md="60" />
    </>
  );
}
